<template>
    <transition 
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
    >
        <div v-if="showOverlay" class="fixed inset-0 z-[100] bg-[#111b21] flex flex-col font-sans">
            <!-- Header -->
            <div class="h-16 flex items-center px-6 justify-between text-white/90 border-b border-white/5">
                <button @click="$emit('close')" class="p-2 hover:bg-white/10 rounded-full transition-colors">
                    <XIcon class="h-6 w-6" />
                </button>
                <div class="flex flex-col items-center">
                    <span class="text-sm font-bold truncate max-w-md">{{ activeFile?.file?.name || activeFile?.name }}</span>
                    <span class="text-xs text-white/70 uppercase tracking-[0.2em] font-black">{{ selectedIndex + 1 }} OF {{ files.length }}</span>
                </div>
                <div class="w-10"></div>
            </div>

            <!-- Main Preview Area -->
            <div class="flex-1 flex items-center justify-center p-8 bg-[#0b141a] overflow-hidden relative">
                <transition name="fade" mode="out-in">
                    <div :key="selectedIndex" class="flex items-center justify-center w-full h-full"> 
                        <div v-if="isImage(activeFile?.file)" class="relative group">
                            <div class="flex flex-col items-center">
                                <img :src="getFileUrl(activeFile?.file)" class="max-h-[50vh] max-w-full rounded-lg shadow-2xl object-contain ring-1 ring-white/10" />
                                <div class="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        @click="showImageEditor = true"
                                        class="p-3 bg-black/40 hover:bg-black/60 backdrop-blur-md text-white rounded-full transition-all border border-white/10 shadow-xl"
                                        :title="$t('chat.editImage')"
                                    >
                                        <PencilIcon class="h-5 w-5" />
                                    </button>
                                </div>
                                <p class="text-sm text-white/40 mt-4 font-medium">{{ formatFileSize(activeFile?.file?.size || activeFile?.size) }} - {{ getFileExt(activeFile?.file?.name || activeFile?.name).toUpperCase() }} file</p>
                            </div>
                        </div>
                        <div v-else-if="isVideo(activeFile?.file)" class="relative w-full h-full flex flex-col items-center justify-center">
                            <video :src="getFileUrl(activeFile?.file)" controls class="max-h-[60vh] max-w-full rounded-xl shadow-2xl ring-1 ring-white/10"></video>
                            <p class="text-sm text-white/40 mt-4 font-medium">{{ formatFileSize(activeFile?.file?.size || activeFile?.size) }} - {{ getFileExt(activeFile?.file?.name || activeFile?.name).toUpperCase() }} file</p>
                        </div>
                        <div v-else-if="isAudio(activeFile?.file)" class="flex flex-col items-center justify-center w-full px-12">
                            <div class="h-48 w-48 bg-[#1e2a30] rounded-full flex items-center justify-center shadow-2xl mb-12 ring-1 ring-white/5">
                                <MicrophoneIcon class="h-20 w-20 text-orange-500 opacity-80" />
                            </div>
                            <audio :src="getFileUrl(activeFile?.file)" controls class="w-full max-w-md"></audio>
                            <p class="text-sm text-white/40 mt-4 font-medium">{{ formatFileSize(activeFile?.file?.size || activeFile?.size) }} - {{ getFileExt(activeFile?.file?.name || activeFile?.name).toUpperCase() }} file</p>
                        </div>
                        <div v-else class="flex flex-col items-center justify-center text-center animate-in zoom-in-95 duration-300">
                            <div class="h-48 w-40 bg-[#1e2a30] rounded-2xl flex flex-col items-center justify-center shadow-2xl relative ring-1 ring-white/5 overflow-hidden">
                                <div class="absolute top-0 inset-x-0 h-1 bg-emerald-500"></div>
                                <div class="p-4 bg-white/5 rounded-full mb-4">
                                    <DocumentTextIcon class="h-16 w-16 text-emerald-500 opacity-80" />
                                </div>
                                <div class="px-4 text-[10px] font-black text-white/30 uppercase tracking-[0.2em] truncate w-full">{{ getFileExt(activeFile?.file?.name || activeFile?.name) }}</div>
                            </div>
                            <h3 class="text-xl font-bold text-white mt-8 mb-2">{{ $t('chat.noPreviewAvailable') }}</h3>
                            <p class="text-sm text-white/40 max-w-xs">{{ formatFileSize(activeFile?.file?.size || activeFile?.size) }} - {{ getFileExt(activeFile?.file?.name || activeFile?.name).toUpperCase() }} file</p>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Message Input & Controls -->
            <div class="px-6 py-4 bg-[#111b21] border-t border-white/5 shadow-2xl">
                <div class="max-w-4xl mx-auto flex items-end bg-[#2a3942] rounded-3xl px-5 py-3 shadow-inner ring-1 ring-white/5 relative min-h-[56px]">
                    <EmojiHappyIcon 
                        ref="emojiButton"
                        @click="showEmojiPicker = !showEmojiPicker"
                        class="h-6 w-6 text-white/40 cursor-pointer hover:text-white/80 transition-colors mr-3 mb-1.5" 
                    />
                    
                    <!-- Emoji Picker Popover for Overlay -->
                    <div v-if="showEmojiPicker" ref="emojiPickerContainer" class="absolute bottom-full left-0 mb-4 z-[110] animate-in fade-in zoom-in-95 duration-200">
                        <EmojiPicker 
                            :native="true" 
                            theme="dark"
                            :static-texts="{ placeholder: 'Search emoji...' }"
                            @select="onSelectEmojiOverlay" 
                            class="emoji-picker-container shadow-2xl border border-white/5 rounded-2xl"
                        />
                    </div>

                    <textarea
                        ref="captionInput"
                        :placeholder="$t('chat.addCaption')"
                        class="flex-1 bg-transparent border-none text-white focus:ring-0 text-sm placeholder:text-white/30 py-1.5 resize-none custom-scrollbar min-h-[32px] max-h-32 block"
                        rows="1"
                        style="scrollbar-width: thin;"
                        :value="activeFile?.caption"
                        @input="handleInput"
                        @keydown.enter.exact.prevent="$emit('send-files')"
                        @keydown.alt.enter.exact.prevent="insertNewline"
                        @keydown.shift.enter.exact.prevent="insertNewline"
                        @paste="onPaste"
                    ></textarea>
                </div>
            </div>

            <!-- Thumbnail List & Send Button -->
            <div class="h-28 bg-[#111b21] flex items-center px-6 border-t border-white/5 space-x-6">
                <div class="flex-1 flex items-center space-x-3 overflow-x-auto pb-2 custom-scrollbar-horizontal h-20">
                    <div 
                        v-for="(item, index) in files" 
                        :key="index"
                        @click="$emit('update:selectedIndex', index)"
                        class="relative h-16 w-16 rounded-xl flex-shrink-0 cursor-pointer transition-all p-1 group/thumb border-2"
                        :class="selectedIndex === index ? 'border-emerald-500 bg-emerald-500/10' : 'border-transparent bg-white/5 hover:bg-white/10'"
                    >
                        <img v-if="isImage(item.file)" :src="getFileUrl(item.file)" class="w-full h-full object-cover rounded-lg" />
                        <div v-else class="w-full h-full bg-[#1e2a30] rounded-lg flex flex-col items-center justify-center p-1">
                            <DocumentTextIcon class="h-5 w-5 text-emerald-500/60" />
                            <span class="text-[8px] text-white/40 font-black uppercase mt-1 tracking-tighter truncate w-full text-center">{{ getFileExt(item.file.name) }}</span>
                        </div>
                        <button 
                            @click.stop="$emit('remove-file', index)" 
                            class="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 shadow-lg opacity-0 group-hover/thumb:opacity-100 transition-opacity transform scale-90"
                        >
                            <XIcon class="h-3 w-3" />
                        </button>
                    </div>
                    
                    <button @click="$emit('trigger-add')" class="h-16 w-16 border-2 border-dashed border-white/10 rounded-xl flex flex-col items-center justify-center text-white/30 hover:text-white/60 hover:border-white/30 hover:bg-white/5 transition-all flex-shrink-0">
                        <PlusIcon class="h-6 w-6" />
                        <span class="text-[8px] font-bold uppercase tracking-widest mt-1">{{ $t('actions.add') }}</span>
                    </button>
                </div>

                <button 
                    @click="$emit('send-files')" 
                    :disabled="isSending"
                    class="h-14 w-14 bg-[#00a884] hover:bg-[#00c99d] rounded-full flex items-center justify-center text-white shadow-xl transition-all hover:scale-105 active:scale-95 group relative mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <div v-if="isSending" class="h-6 w-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <PaperAirplaneIcon v-else class="h-6 w-6 rotate-45 transform translate-x-0.5 -translate-y-0.5" />
                    
                    <div v-if="!isSending" class="absolute -top-1.5 -right-1.5 bg-white text-[#00a884] text-[10px] font-black h-6 w-6 rounded-full flex items-center justify-center border-2 border-[#111b21] shadow-md">
                        {{ files.length }}
                    </div>
                </button>
            </div>

            <!-- Image Editor Modal -->
            <ImageEditor 
                :show="showImageEditor"
                :imageFile="activeFile?.file"
                @cancel="showImageEditor = false"
                @save="onSaveEditedImage"
            />
        </div>
    </transition>
</template>

<script>
import { 
    XIcon, 
    DocumentTextIcon, 
    EmojiHappyIcon, 
    PlusIcon, 
    PaperAirplaneIcon,
    MicrophoneIcon,
    PhotographIcon,
    VideoCameraIcon,
    PencilIcon
} from "@heroicons/vue/outline";
import EmojiPicker from 'vue3-emoji-picker';
import 'vue3-emoji-picker/css';
import ImageEditor from '../Modals/ImageEditor.vue';

export default {
    name: 'ChatPreviewOverlay',
    components: {
        XIcon,
        DocumentTextIcon,
        EmojiHappyIcon,
        PlusIcon,
        PaperAirplaneIcon,
        MicrophoneIcon,
        PhotographIcon,
        VideoCameraIcon,
        PencilIcon,
        EmojiPicker,
        ImageEditor
    },
    props: {
        showOverlay: Boolean,
        files: Array,
        selectedIndex: Number,
        isSending: Boolean
    },
    emits: ['close', 'update:selectedIndex', 'remove-file', 'trigger-add', 'send-files', 'update-caption', 'paste-files', 'update-file'],
    data() {
        return {
            showEmojiPicker: false,
            showImageEditor: false
        };
    },
    computed: {
        activeFile() {
            return this.files[this.selectedIndex];
        }
    },
    watch: {
        selectedIndex() {
            this.$nextTick(() => {
                this.autoResize();
            });
        },
        showOverlay(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.autoResize();
                });
            }
        }
    },
    mounted() {
        document.addEventListener('mousedown', this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    },
    methods: {
        onPaste(event) {
            const items = (event.clipboardData || event.originalEvent?.clipboardData)?.items;
            if (!items) return;
            const files = [];
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf("image") !== -1) {
                    const blob = items[i].getAsFile();
                    if (blob) files.push(blob);
                }
            }
            if (files.length > 0) {
                event.preventDefault();
                this.$emit('paste-files', files);
            }
        },
        handleClickOutside(event) {
            if (this.showEmojiPicker) {
                const picker = this.$refs.emojiPickerContainer;
                const button = this.$refs.emojiButton?.$el || this.$refs.emojiButton;
                
                if (picker && !picker.contains(event.target) && 
                    button && !button.contains(event.target)) {
                    this.showEmojiPicker = false;
                }
            }
        },
        isImage(file) {
            if (!file) return false;
            return file.type && file.type.startsWith('image/');
        },
        isVideo(file) {
            if (!file) return false;
            return file.type && file.type.startsWith('video/');
        },
        isAudio(file) {
            if (!file) return false;
            return file.type && file.type.startsWith('audio/');
        },
        getFileUrl(file) {
            if (!file) return '';
            return URL.createObjectURL(file);
        },
        getFileExt(filename) {
            if (!filename) return '';
            return filename.split('.').pop().toLowerCase();
        },
        formatFileSize(bytes) {
            if (!bytes || isNaN(bytes)) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        onSelectEmojiOverlay(emoji) {
            const input = this.$refs.captionInput;
            const currentCaption = this.activeFile?.caption || '';
            
            if (input) {
                const start = input.selectionStart;
                const end = input.selectionEnd;
                const newCaption = currentCaption.substring(0, start) + emoji.i + currentCaption.substring(end);
                this.$emit('update-caption', this.selectedIndex, newCaption);
                
                this.$nextTick(() => {
                    input.focus();
                    input.setSelectionRange(start + emoji.i.length, start + emoji.i.length);
                    this.autoResize();
                });
            } else {
                this.$emit('update-caption', this.selectedIndex, currentCaption + emoji.i);
            }
        },
        handleInput(event) {
            this.$emit('update-caption', this.selectedIndex, event.target.value);
            this.autoResize();
        },
        autoResize() {
            const input = this.$refs.captionInput;
            if (input) {
                input.style.height = 'auto';
                input.style.height = Math.min(input.scrollHeight, 128) + 'px';
            }
        },
        insertNewline() {
            const input = this.$refs.captionInput;
            if (input) {
                const currentCaption = this.activeFile?.caption || '';
                const start = input.selectionStart;
                const end = input.selectionEnd;
                const newCaption = currentCaption.substring(0, start) + "\n" + currentCaption.substring(end);
                this.$emit('update-caption', this.selectedIndex, newCaption);
                
                this.$nextTick(() => {
                    input.focus();
                    input.setSelectionRange(start + 1, start + 1);
                    this.autoResize();
                });
            }
        },
        onSaveEditedImage(editedFile) {
            this.showImageEditor = false;
            this.$emit('update-file', this.selectedIndex, editedFile);
        }
    }
}
</script>
